import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import AnimatedPage from '@/components/AnimatedPage';
import { useNotification } from '@/contexts/NotificationContext';
import {
  appealAutomationSettingsService,
  AppealAutomationSettings,
} from '@/services/settings/appealAutomation.service';

const Settings: React.FC = () => {
  const { showNotification } = useNotification();
  const [settings, setSettings] = useState<AppealAutomationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requestedState, setRequestedState] = useState<boolean | null>(null);

  useEffect(() => {
    appealAutomationSettingsService
      .get()
      .then(setSettings)
      .catch(() => showNotification('Не удалось загрузить настройки апелляций', 'error'))
      .finally(() => setLoading(false));
  }, [showNotification]);

  const handleToggle = async (enabled: boolean) => {
    setSaving(true);
    try {
      const updated = await appealAutomationSettingsService.update(enabled);
      setSettings(updated);
      setRequestedState(null);
      showNotification(
        enabled
          ? 'Автоматическое закрытие апелляций включено'
          : 'Автоматическое закрытие апелляций выключено',
        enabled ? 'success' : 'warning'
      );
    } catch {
      showNotification('Не удалось изменить настройку', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <AnimatedPage>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Настройки</Typography>
        <Card variant="outlined" sx={{ maxWidth: 720 }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Автоматизация апелляций</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings?.auto_close_enabled ?? false}
                    disabled={saving}
                    onChange={(event) => setRequestedState(event.target.checked)}
                  />
                }
                label="Автоматически закрывать апелляции по SLA"
              />
              <Alert severity={settings?.auto_close_enabled ? 'success' : 'warning'}>
                {settings?.auto_close_enabled
                  ? 'Автозакрытие включено. Реакции и SLA обрабатываются штатно.'
                  : 'Автозакрытие по SLA выключено. Реакции и синхронизация продолжают работать.'}
              </Alert>
              {settings?.date_update && (
                <Typography variant="caption" color="text.secondary">
                  Последнее изменение: {new Date(settings.date_update).toLocaleString('ru-RU')}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
      <Dialog
        open={requestedState !== null}
        onClose={() => !saving && setRequestedState(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {requestedState ? 'Включить автозакрытие?' : 'Выключить автозакрытие?'}
        </DialogTitle>
        <DialogContent>
          <Alert severity={requestedState ? 'warning' : 'info'}>
            {requestedState
              ? 'После включения все уже просроченные и подходящие под лимит апелляции могут быть закрыты на ближайшем цикле обработки.'
              : 'SLA-закрытия будут приостановлены. Реакции трейдеров и синхронизация апелляций продолжат работать.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestedState(null)} disabled={saving}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color={requestedState ? 'warning' : 'primary'}
            disabled={saving || requestedState === null}
            onClick={() => requestedState !== null && handleToggle(requestedState)}
          >
            Подтвердить
          </Button>
        </DialogActions>
      </Dialog>
    </AnimatedPage>
  );
};

export default Settings;
